/**
 * Story 10.10 — dedup do evento de conversão por pedido.
 *
 * A confirmação é uma página que o comprador revisita (F5, voltar, e sobretudo o LINK DO
 * E-MAIL, dias depois e noutra aba). Sem memória, cada visita mediria uma compra nova e a
 * RECEITA MEDIDA duplicaria. Por isso a marca vive em `localStorage` e não em
 * `sessionStorage`: este último morre com a aba, que é exatamente a fronteira que o link do
 * e-mail atravessa — o alcance certo é o dispositivo, não a aba.
 *
 * Fica em `.ts` (e não dentro do componente) para correr sob o `environment: node` do vitest.
 */

const TRACKED_PURCHASE_KEY_PREFIX = "ve:analytics:purchase:"
const TRACKED_MARK = "1"

export function trackedPurchaseKey(orderId: string): string {
  return `${TRACKED_PURCHASE_KEY_PREFIX}${orderId}`
}

/**
 * Já medimos este pedido neste browser? `false` sempre que o storage não estiver disponível
 * (modo privado, storage cheio): a degradação escolhida é medir a mais em vez de perder a
 * conversão — e nunca rebentar a página de confirmação.
 */
export function hasTrackedPurchase(orderId: string): boolean {
  const storage = readLocalStorage()
  if (!storage) {
    return false
  }
  try {
    return storage.getItem(trackedPurchaseKey(orderId)) === TRACKED_MARK
  } catch {
    // Acesso ao storage lança em modo privado de alguns browsers — tratado como "ainda não
    // medido" (ver acima), nunca propagado: analítica não pode derrubar a confirmação.
    return false
  }
}

export function markPurchaseTracked(orderId: string): void {
  const storage = readLocalStorage()
  if (!storage) {
    return
  }
  try {
    storage.setItem(trackedPurchaseKey(orderId), TRACKED_MARK)
  } catch {
    // Storage cheio ou bloqueado: sem marca, uma revisita volta a medir. É o mesmo
    // compromisso deliberado de `hasTrackedPurchase` — melhor do que estourar a página.
  }
}

/**
 * Nomes das chaves de dedup presentes numa lista de chaves. Puro e testável — a casca impura é
 * `clearTrackedPurchases`.
 */
export function listTrackedPurchaseKeys(keys: readonly string[]): string[] {
  return keys.filter((key) => key.startsWith(TRACKED_PURCHASE_KEY_PREFIX))
}

/**
 * Apaga as marcas de conversão deste browser (Story 10.10 — a retirada tem de RETIRAR).
 *
 * Cada marca é um identificador de primeira parte que liga ESTE dispositivo a ids de pedido
 * concretos e não expira nunca: sobreviver a uma retirada de consentimento contradiria a própria
 * retirada. Best-effort, como o resto do módulo — nunca rebenta a página.
 */
export function clearTrackedPurchases(): void {
  const storage = readLocalStorage()
  if (!storage) {
    return
  }
  try {
    for (const key of listTrackedPurchaseKeys(Object.keys(storage))) {
      storage.removeItem(key)
    }
  } catch {
    // Storage bloqueado: sem marcas para apagar ou sem permissão para o fazer. Igual ao resto
    // do módulo — a limpeza é best-effort e não pode custar a página.
  }
}

// SSR-safe e tolerante a browsers que expõem `localStorage` mas lançam ao lê-lo.
function readLocalStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null
  }
  try {
    return window.localStorage ?? null
  } catch {
    return null
  }
}
